import { useBlocker, useNavigate } from "react-router-dom";
import { useCallback, useEffect } from "react";
import { useLeaveDialogContext } from "../containers/_common/useLeaveDialogContext";
import { useDispatch, useStore } from "react-redux";
import { getEditFormDirtyFlag } from "../redux/reducers/appReducer";
import { setEditFormIsDirty } from "../redux/actions/actions";

export const useCustomRedirectPrompt = () => {
  const navigate = useNavigate();

  const {
    showPrompt,
    togglePrompt,
    deferredCallback,
    confirmedNavigation,
    lastLocation,
    toggleConfirmedNavigation,
    changeLastLocation,
    showDialogBeforeChangeUrl,
  } = useLeaveDialogContext();

  const store = useStore();
  const dispatch = useDispatch();

  const hideDialogBeforeChangeUrl = () => {
    togglePrompt(false);
  };

  const cancelNavigation = useCallback(() => {
    if (deferredCallback.current) deferredCallback.current = null;
    hideDialogBeforeChangeUrl();
    changeLastLocation(null);
  }, []);

  // Navigation away from a dirty form is cancelled and the leave dialog shown; confirming it clears the dirty flag
  // and navigates to the remembered location. The flag is read from the store when navigation happens, because save
  // handlers clear it and navigate in the same tick, before this component re-renders.
  const blocker = useBlocker(() => getEditFormDirtyFlag(store.getState()) && !confirmedNavigation);

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    const nextLocation = blocker.location;
    blocker.reset();
    if (nextLocation.pathname === lastLocation?.pathname) return;
    showDialogBeforeChangeUrl();
    changeLastLocation(nextLocation);
  }, [blocker.state]);

  const confirmNavigation = () => {
    if (deferredCallback.current) deferredCallback.current();
    dispatch(setEditFormIsDirty(false));
    hideDialogBeforeChangeUrl();
    toggleConfirmedNavigation(true);
    deferredCallback.current = null;
  };

  useEffect(() => {
    if (confirmedNavigation) {
      if (lastLocation) navigate(lastLocation.pathname);
      changeLastLocation(null);
      toggleConfirmedNavigation(false);
    }
  }, [confirmedNavigation, lastLocation]);

  return { showPrompt, confirmNavigation, cancelNavigation };
};
